
'use client';
// Return Nepali short form for pakshya (शु for शुक्ल, कृ for कृष्ण)
function getPakshyaShortForm(pakshya: string): React.ReactNode {
  if (!pakshya) return null;
  if (pakshya === 'S' || pakshya === 'शुक्ल' || pakshya === 'शुक्लपक्ष') return 'शु';
  if (pakshya === 'K' || pakshya === 'कृष्ण' || pakshya === 'कृष्णपक्ष') return 'कृ';
  return pakshya;
}

import { useState, useEffect, useMemo, memo, useCallback, useRef } from 'react';
import { useRouter } from 'next/navigation';
import { apiService } from '@/lib/api';
import { useDate } from '@/contexts/DateContext';
import { useAuth } from '@/contexts/AuthContext';
import { ChevronLeft, ChevronRight, Search } from 'lucide-react';
import type { CalendarDay, InitialCalendarData, InitialDateData } from '@/types/calendar';
import { computeEnglishMonthInfo } from '@/lib/calendar';
import { extractPanchangaValue } from '@/lib/safeRender';

const nepaliMonths = [
  { name: 'बैशाख', num: 1 },
  { name: 'जेष्ठ', num: 2 },
  { name: 'आषाढ', num: 3 },
  { name: 'श्रावण', num: 4 },
  { name: 'भाद्र', num: 5 },
  { name: 'आश्विन', num: 6 },
  { name: 'कार्तिक', num: 7 },
  { name: 'मंसिर', num: 8 },
  { name: 'पौष', num: 9 },
  { name: 'माघ', num: 10 },
  { name: 'फाल्गुन', num: 11 },
  { name: 'चैत्र', num: 12 }
];

const weekdays = [
  { en: 'Sun', ne: 'आइत' },
  { en: 'Mon', ne: 'सोम' },
  { en: 'Tue', ne: 'मंगल' },
  { en: 'Wed', ne: 'बुध' },
  { en: 'Thu', ne: 'बिहि' },
  { en: 'Fri', ne: 'शुक्र' },
  { en: 'Sat', ne: 'शनि' },
];

type ReminderAlert = {
  reminderId: string;
  title: string;
  message: string;
  trigger: string;
};

type CalendarReminder = {
  id: string;
  userKey: string;
  nepaliDate: string;
  englishDate: string;
  note: string;
  reminderTime: string;
  notifyTwoDays: boolean;
  notifyOneDay: boolean;
  notifySameDay: boolean;
  snoozedUntil?: string | null;
  createdAt: string;
  updatedAt: string;
};

const REMINDER_STORAGE_KEY = 'swasti_calendar_reminders_v1';
const REMINDER_SENT_STORAGE_KEY = 'swasti_calendar_reminder_sent_v1';

const readRemindersFromStorage = (): CalendarReminder[] => {
  if (typeof window === 'undefined') return [];
  try {
    const raw = localStorage.getItem(REMINDER_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const writeRemindersToStorage = (items: CalendarReminder[]) => {
  if (typeof window === 'undefined') return;
  localStorage.setItem(REMINDER_STORAGE_KEY, JSON.stringify(items));
};

const readReminderSentMap = (): Record<string, boolean> => {
  if (typeof window === 'undefined') return {};
  try {
    const raw = localStorage.getItem(REMINDER_SENT_STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
};

const writeReminderSentMap = (map: Record<string, boolean>) => {
  if (typeof window === 'undefined') return;
  localStorage.setItem(REMINDER_SENT_STORAGE_KEY, JSON.stringify(map));
};

// Format date for Google Calendar
const formatGoogleDateTime = (input: Date) => {
  const year = input.getFullYear();
  const month = String(input.getMonth() + 1).padStart(2, '0');
  const day = String(input.getDate()).padStart(2, '0');
  const hh = String(input.getHours()).padStart(2, '0');
  const mm = String(input.getMinutes()).padStart(2, '0');
  const ss = String(input.getSeconds()).padStart(2, '0');
  return `${year}${month}${day}T${hh}${mm}${ss}`;
};

// Convert number to Devanagari numerals
const toDevanagariNumeral = (num: number): string => {
  const devanagariDigits = ['०', '१', '२', '३', '४', '५', '६', '७', '८', '९'];
  return num.toString().split('').map(digit => devanagariDigits[parseInt(digit)]).join('');
};

// Memoized Calendar Day Cell Component for better INP
const CalendarDayCell = memo(({
    day,
    index,
    isToday,
    isSaturday,
    isSelected,
    nepaliDayNumeral,
    englishDay,
    hasEvents,
    currentMonth,
    currentYear,
    onDateSelect
  }: {
    day: CalendarDay;
    index: number;
    isToday: boolean;
    isSaturday: boolean;
    isSelected: boolean;
    nepaliDayNumeral: string;
    englishDay: string;
    hasEvents: boolean;
    currentMonth: number;
    currentYear: number;
    onDateSelect: (date: {year: number, month: number, day: number}) => void;
  }) => {
    const router = useRouter();
    const { setDateData } = useDate();

    const handleClick = useCallback(() => {
      if (!day.nepali) return;

      const nepaliYear = currentYear;
      const nepaliMonth = currentMonth;
      const nepaliDay = day.nepali.day;

      // Set selected date for visual highlighting
      onDateSelect({ year: nepaliYear, month: nepaliMonth, day: Number(nepaliDay) });

      // Format date as YYYY-MM-DD
      const nepaliDateStr = `${nepaliYear}-${String(nepaliMonth).padStart(2, '0')}-${String(nepaliDay).padStart(2, '0')}`;

      // Update DateContext so PanchangaSidebar reacts to selected date
      const nepaliObj = { year: nepaliYear, month: nepaliMonth, day: nepaliDay, monthName: nepaliMonths[nepaliMonth - 1]?.name || '' };
      let englishObj: any = null;
      if (day.english) {
        if (typeof day.english === 'string') {
          const parts = day.english.split('-');
          if (parts.length === 3) englishObj = { year: Number(parts[0]), month: Number(parts[1]), day: Number(parts[2]) };
        } else if (typeof day.english === 'object' && 'date' in day.english && day.english.date) {
          const parts = String(day.english.date).split('-');
          if (parts.length === 3) englishObj = { year: Number(parts[0]), month: Number(parts[1]), day: Number(parts[2]) };
        } else if (typeof day.english === 'object') {
          const e = day.english as any;
          if (e.year && e.month && e.day) englishObj = { year: Number(e.year), month: Number(e.month), day: Number(e.day) };
        }
      }
      if (englishObj) {
        setDateData({ nepali: nepaliObj, english: englishObj, timestamp: new Date().toISOString() });
      }
    }, [day, hasEvents, currentMonth, currentYear, onDateSelect, setDateData]);

    const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        handleClick();
      }
    }, [handleClick]);

    // Determine background and text colors based on date type
    let bgClass = '';
    let borderClass = '';
    let nepaliTextClass = '';
    let englishTextClass = '';

    if (isToday) {
      // Today date - solid brand primary, no gradient, matches site's toned-down palette
      bgClass = 'bg-primary-600 dark:bg-primary-700';
      borderClass = 'border-primary-700 dark:border-primary-800 ring-2 ring-primary-500/40 dark:ring-primary-600/40';
      nepaliTextClass = 'text-white dark:text-white';
      englishTextClass = 'text-white dark:text-white';
    } else if (isSelected) {
      // Selected date - keep border color unchanged
      bgClass = 'bg-[#0d9488]';
      borderClass = '';
      nepaliTextClass = 'text-white dark:text-white';
      englishTextClass = 'text-white dark:text-white';
    } else if (isSaturday) {
      // Saturday - subtle red tint instead of a solid maroon slab
      bgClass = 'bg-primary-50 dark:bg-dark-800 md:bg-primary-50 md:dark:bg-primary-950/30';
      borderClass = 'border-primary-200 dark:border-primary-800';
      nepaliTextClass = 'text-primary-700 dark:text-primary-400';
      englishTextClass = 'text-primary-600 dark:text-primary-400 font-medium';
    } else {
      // Regular date
      bgClass = 'bg-white dark:bg-dark-800';
      borderClass = 'border-gray-300 dark:border-gray-600 hover:border-primary-300 dark:hover:border-primary-700';
      nepaliTextClass = 'text-gray-900 dark:text-white';
      englishTextClass = 'text-slate-600 dark:text-slate-400 font-medium'; // Softer blue-gray color
    }

    // Remove placeholder getPakshyaShortForm to prevent runtime error
    // If needed, implement or import the real function here.

    return (
      <div
        key={index}
        data-is-today={isToday}
        className={`aspect-square w-full p-1.5 sm:p-2.5 md:p-4 lg:p-5 border-2 rounded-lg sm:rounded-xl transition-all duration-200 cursor-pointer relative group overflow-visible hover:scale-105 ${bgClass} ${
          isToday
            ? 'border-primary-700 dark:border-primary-800 shadow-md z-10'
            : isSelected
            ? 'border-blue-500 shadow-lg shadow-blue-200 dark:shadow-blue-900/30 z-10'
            : isSaturday
            ? 'border-primary-200 dark:border-primary-800 hover:shadow-md'
            : 'border-gray-200 dark:border-gray-700 hover:border-accent-300 dark:hover:border-accent-600 hover:shadow-md'
        }`}
        role="button"
        tabIndex={0}
        aria-label={`${String(day.nepali?.day || '')} ${nepaliMonths[currentMonth - 1]?.name || `Month ${currentMonth}`} ${currentYear}${hasEvents ? ', has events' : ''}${isToday ? ', today' : ''}`}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
      >
        {/* Nepali Date - centered inside the cell (always visible) */}
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none w-full h-full">
          <div className={`text-base sm:text-lg md:text-2xl lg:text-3xl font-bold devanagari drop-shadow-md ${nepaliTextClass}`}>
            {nepaliDayNumeral || ''}
          </div>
        </div>

        {/* Top center: Event/Festival text - hidden on mobile */}
        <div className="hidden sm:block absolute top-1 left-1/2 transform -translate-x-1/2 z-10 w-[calc(100%-8px)] text-center">
          {day.calendarCellData?.event && (
            <div className={`text-[7px] sm:text-[8px] md:text-[10px] lg:text-[12px] font-semibold devanagari leading-tight line-clamp-2 ${isToday ? 'text-white drop-shadow' : isSaturday ? 'text-primary-700 dark:text-primary-400' : 'text-gray-700 dark:text-gray-300'}`}>
              {String(day.calendarCellData.event)}
            </div>
          )}
        </div>

        {/* Middle-left: Pakshya - hidden on mobile */}
        {(() => {
          const rawPak = day.calendarCellData?.pakshya || (day.events && day.events.length > 0 && day.events[0].pakshya) || '';
          const isShukla = rawPak === 'S' || rawPak === 'शुक्ल' || rawPak === 'शुक्लपक्ष';
          const pakCls = isToday ? 'text-white/90' : isSaturday ? 'text-primary-600 dark:text-primary-400' : isShukla ? 'text-amber-600 dark:text-amber-400' : 'text-indigo-600 dark:text-indigo-400';
          const thitiCls = isToday ? 'text-white/90' : isSaturday ? 'text-primary-700 dark:text-primary-300' : isShukla ? 'text-amber-700 dark:text-amber-300' : 'text-indigo-700 dark:text-indigo-300';
          const thitiVal = extractPanchangaValue(day.calendarCellData?.thiti) || (day.events && day.events.length > 0 ? extractPanchangaValue(day.events[0].thiti) : '') || extractPanchangaValue(day.thiti) || '';
          return (
            <>
              {rawPak && (
                <div className="hidden sm:flex absolute left-1 top-1/2 transform -translate-y-1/2 z-10">
                  <div className={`text-[7px] sm:text-[8px] md:text-[9px] lg:text-[10px] font-bold devanagari ${pakCls}`}>
                    {getPakshyaShortForm(String(rawPak))}
                  </div>
                </div>
              )}
              {thitiVal && (
                <div className="hidden sm:flex absolute bottom-1 left-1 z-10">
                  <div className={`text-[7px] sm:text-[8px] md:text-[9px] lg:text-[10px] devanagari font-semibold ${thitiCls}`}>
                    {thitiVal}
                  </div>
                </div>
              )}
            </>
          );
        })()}

        {/* Bottom-right: English Date - always visible, larger and bolder on mobile for Saturday */}
        {englishDay && (
          <div
            className={`absolute bottom-0.5 right-0.5 font-bold z-40 sm:px-2 md:px-2.5 lg:px-3 sm:py-0.5 md:py-1 lg:py-1.5 sm:rounded-sm whitespace-nowrap
              text-[10px] sm:text-[12px] md:text-[14px] lg:text-[16px]
              ${englishTextClass}
            `}
            style={{
              minWidth: '1.2em',
              maxWidth: '2.2em',
              overflow: 'visible',
              // Remove background and border for all cells, including Saturday
              // Increase font size for all
            }}
          >
            {typeof englishDay === 'string' ? englishDay : String(englishDay || '')}
          </div>
        )}

        {/* Event counter badge - hidden on mobile */}
        {hasEvents && (
          <div className={`hidden sm:flex absolute top-1 right-1 sm:top-1.5 sm:right-1.5 md:top-2 md:right-2 items-center justify-center w-5 h-5 sm:w-6 sm:h-6 rounded-full z-30 text-[6px] sm:text-[7px] font-bold ${
            isToday || isSelected || isSaturday
              ? 'bg-white text-gray-900 shadow-lg'
              : 'bg-gradient-to-r from-teal-500 to-emerald-500 dark:from-teal-600 dark:to-emerald-600 text-white shadow-md'
          }`}>
            {day.events && Array.isArray(day.events) && day.events.length > 0 ? (
              day.events.length === 1 ? '1' : `1+${day.events.length - 1}`
            ) : '1'}
          </div>
        )}
      </div>
    );
  });



interface CalendarGridProps {
  initialCalendarData?: InitialCalendarData | null;
  initialDateData?: InitialDateData | null;
}

function CalendarGrid({ initialCalendarData, initialDateData }: CalendarGridProps) {
  const { user, isAuthenticated } = useAuth();
  const router = useRouter();
  const [currentMonth, setCurrentMonth] = useState<number>(initialCalendarData?.month || 0);
  const [currentYear, setCurrentYear] = useState<number>(initialCalendarData?.year || 0);
  const [selectedMonth, setSelectedMonth] = useState<number>(initialCalendarData?.month || 0);
  const [selectedYear, setSelectedYear] = useState<number>(initialCalendarData?.year || 0);

  // Fetch current Nepali date from backend if not provided
  useEffect(() => {
    if (!initialCalendarData) {
      fetch('/api/calendar/current')
        .then(res => res.json())
        .then(data => {
          if (data && data.data && data.data.nepali) {
            setCurrentYear(data.data.nepali.year);
            setCurrentMonth(data.data.nepali.month);
            setSelectedYear(data.data.nepali.year);
            setSelectedMonth(data.data.nepali.month);
          }
        });
    }
  }, [initialCalendarData]);
  const [selectedDate, setSelectedDate] = useState<{year: number, month: number, day: number} | null>(null);
  const [eventPopupOpen, setEventPopupOpen] = useState(false);
  const [selectedDateEvents, setSelectedDateEvents] = useState<any[]>([]);
  const [englishYear, setEnglishYear] = useState(initialCalendarData?.englishYear ?? (initialCalendarData?.year ? initialCalendarData.year - 57 : 0));
  const [englishMonthRange, setEnglishMonthRange] = useState<string>(initialCalendarData?.englishMonthRange || '');
  const [calendarData, setCalendarData] = useState<(CalendarDay | null)[]>(initialCalendarData?.grid || []);
  const [loading, setLoading] = useState(!(initialCalendarData?.grid?.length));
  const [error, setError] = useState('');
  const [initialized, setInitialized] = useState(!!initialCalendarData?.grid?.length);
  const [shouldScrollToToday, setShouldScrollToToday] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const [reminders, setReminders] = useState<CalendarReminder[]>([]);
  const [selectedDateInfo, setSelectedDateInfo] = useState<{ nepaliDate: string; englishDate: string } | null>(null);
  const [reminderModalOpen, setReminderModalOpen] = useState(false);
  const [editingReminderId, setEditingReminderId] = useState<string | null>(null);
  const [noteInput, setNoteInput] = useState('');
  const [reminderTime, setReminderTime] = useState('09:00');
  const [notifyTwoDays, setNotifyTwoDays] = useState(true);
  const [notifyOneDay, setNotifyOneDay] = useState(true);
  const [notifySameDay, setNotifySameDay] = useState(true);
  const [reminderError, setReminderError] = useState('');
  const [activeReminderAlert, setActiveReminderAlert] = useState<ReminderAlert | null>(null);
  const skipFirstFetchRef = useRef(!!initialCalendarData?.grid?.length);

  const userReminderKey = useMemo(() => {
    if (!user) return '';
    if ((user as any).id) return `u:${(user as any).id}`;
    if ((user as any).email) return `e:${(user as any).email}`;
    return '';
  }, [user]);

  const persistUserReminders = useCallback((updater: (current: CalendarReminder[]) => CalendarReminder[]) => {
    if (!userReminderKey) return;
    const allItems = readRemindersFromStorage();
    const currentUserItems = allItems.filter((item) => item.userKey === userReminderKey);
    const updatedUserItems = updater(currentUserItems);
    const merged = [...allItems.filter((item) => item.userKey !== userReminderKey), ...updatedUserItems];
    writeRemindersToStorage(merged);
    setReminders(updatedUserItems.sort((a, b) => a.englishDate.localeCompare(b.englishDate)));
    // Notify other views (e.g. Panchanga sidebar bell) to refresh
    if (typeof window !== 'undefined') {
      try { window.dispatchEvent(new CustomEvent('calendar:remindersUpdated')); } catch {}
    }
  }, [userReminderKey]);

  const openReminderEditorForDate = useCallback((nepaliDate: string, englishDate: string) => {
    const existing = reminders.find(
      (item) => item.nepaliDate === nepaliDate && item.englishDate === englishDate
    );

    if (existing) {
      setEditingReminderId(existing.id);
      setNoteInput(existing.note);
      setReminderTime(existing.reminderTime || '09:00');
      setNotifyTwoDays(existing.notifyTwoDays);
      setNotifyOneDay(existing.notifyOneDay);
      setNotifySameDay(existing.notifySameDay);
    } else {
      setEditingReminderId(null);
      setNoteInput('');
      setReminderTime('09:00');
      setNotifyTwoDays(true);
      setNotifyOneDay(true);
      setNotifySameDay(true);
    }

    setReminderError('');
    setReminderModalOpen(true);
  }, [reminders]);
  
  // Generate years list dynamically (2070 to 2095)
  const yearsList = useMemo(() => {
    return Array.from({ length: 26 }, (_, i) => 2070 + i);
  }, []);

  // Helper function to format year with English year range
  // Nepali year spans from mid-April of (year - 57) to mid-April of (year - 56)
  const formatYearWithEnglishRange = (nepaliYear: number): string => {
    const startEnglishYear = nepaliYear - 57;
    const endEnglishYear = nepaliYear - 56;
    const endYearShort = endEnglishYear.toString().slice(-2); // Last 2 digits
    return `${nepaliYear}(${startEnglishYear}/${endYearShort})`;
  };

  const { dateData } = useDate();

  useEffect(() => {
    // Initialize with context date when available
    if (dateData?.nepali) {
      const year = dateData.nepali.year;
      const month = dateData.nepali.month;
      setCurrentYear(year);
      setCurrentMonth(month);
      setSelectedYear(year);
      setSelectedMonth(month);
      
      if (dateData.english) {
        setEnglishYear(dateData.english.year);
      } else {
        setEnglishYear(year - 57);
      }
      setInitialized(true);
      return;
    }

    // Fallback to initial date data if provided and not yet initialized
    if (!initialized && initialDateData?.nepali) {
      const year = initialDateData.nepali.year;
      const month = initialDateData.nepali.month;
      setCurrentYear(year);
      setCurrentMonth(month);
      setSelectedYear(year);
      setSelectedMonth(month);
      if (initialDateData.english?.year) {
        setEnglishYear(initialDateData.english.year);
      } else {
        setEnglishYear(year - 57);
      }
      setInitialized(true);
    }
  }, [dateData, initialDateData, initialized]);

  const updateEnglishYearFromCalendarData = useCallback((gridData: (CalendarDay | null)[], nepaliYear: number) => {
    const { englishYear: computedYear, englishMonthRange: computedRange } = computeEnglishMonthInfo(
      gridData,
      nepaliYear
    );
    setEnglishYear(computedYear);
    setEnglishMonthRange(computedRange);
  }, []);
  
  const loadCalendarData = useCallback(async (isRetry = false, forceReload = false) => {
    // Don't load if we already have data and this is not a retry or forced reload
    if (!isRetry && !forceReload && calendarData.length > 0) {
      return;
    }
    
    if (!isRetry) {
      setLoading(true);
    }
    setError('');
    try {
      const response: any = await apiService.getMonthGrid(currentYear, currentMonth);
      
      // Debug: Log response in development
      if (process.env.NODE_ENV === 'development') {
        console.log('Calendar API Response:', response);
      }
      
      if (response?.success && response?.data) {
        // Backend returns grid array directly in data.grid
        // The grid includes null values for empty cells before month starts
        const gridData = response.data.grid || [];
        
        // Debug: Log grid data in development
        if (process.env.NODE_ENV === 'development') {
          console.log('Calendar Grid Data:', gridData);
          console.log('Grid Length:', gridData.length);
        }
        
        if (gridData.length === 0) {
          // Don't show error if we have initial data, just log it
          if (calendarData.length === 0) {
            setError('No calendar data available for this month');
          }
        } else {
          // Show calendar grid immediately with basic data
          setCalendarData(gridData);
          updateEnglishYearFromCalendarData(gridData, currentYear);
          setLoading(false);
          
          // Load additional data (festivals and calendar cell data) in the background
          Promise.all([
            // Fetch festivals for relevant years
            (async () => {
              try {
                let englishYearsForFestivals: number[] = [];
                
                // Extract English years from grid data
                gridData.forEach((cell: any) => {
                  if (cell?.english?.year) {
                    const engYear = Number(cell.english.year);
                    if (!isNaN(engYear) && !englishYearsForFestivals.includes(engYear)) {
                      englishYearsForFestivals.push(engYear);
                    }
                  }
                });
                
                // If we couldn't extract years, estimate from Nepali year
                if (englishYearsForFestivals.length === 0) {
                  const estimatedEngYear = currentYear - 57;
                  englishYearsForFestivals = [estimatedEngYear, estimatedEngYear + 1];
                }
                
                // Load festivals data for all relevant years in parallel
                const festivalsMap: Record<string, any> = {};
                const festivalPromises = englishYearsForFestivals.map(async (year) => {
                  const festivalsResp: any = await apiService.getFestivals(year);
                  if (festivalsResp?.success && Array.isArray(festivalsResp.data)) {
                    festivalsResp.data.forEach((festival: any) => {
                      if (festival?.eng_date) {
                        festivalsMap[festival.eng_date] = festival;
                      }
                    });
                  }
                });
                
                await Promise.all(festivalPromises);
                return festivalsMap;
              } catch (err) {
                console.warn('Failed to load festivals data:', err);
                return {};
              }
            })(),
            
            // Fetch calendar cell data
            (async () => {
              try {
                const cellResp: any = await apiService.getCalendarCellData(currentYear, currentMonth);
                const cellList = (cellResp && cellResp.data && Array.isArray(cellResp.data)) ? cellResp.data : [];
                const cellMap: Record<number, any> = {};
                cellList.forEach((c: any) => {
                  if (!c || !c.nepali_full_date) return;
                  const parts = String(c.nepali_full_date).split('-');
                  const d = Number(parts[2]);
                  if (!isNaN(d)) cellMap[d] = c;
                });
                return cellMap;
              } catch (err) {
                console.warn('Failed to load calendar cell data:', err);
                return {};
              }
            })()
          ]).then(([festivalsMap, cellMap]) => {
            // Merge festivals and calendar cell data into grid
            const enrichedGrid = gridData.map((cell: any) => {
              if (!cell || !cell.nepali || !cell.nepali.day) return cell;
              const d = Number(cell.nepali.day);
              
              // Check if there's a festival on this English date
              let festivalData = null;
              if (cell.english?.date) {
                festivalData = festivalsMap[cell.english.date] || null;
              }
              
              return { 
                ...cell, 
                calendarCellData: cellMap[d] || null,
                festival: festivalData
              };
            });

            setCalendarData(enrichedGrid);
            setError('');
            setRetryCount(0);
          }).catch(err => {
            console.warn('Error enriching calendar data:', err);
            // Keep the basic grid data even if enrichment fails
          });
        }
      } else {
        const errorMsg = response?.message || response?.error || 'Failed to load calendar';
        // Only set error if we don't have any calendar data to show
        if (calendarData.length === 0 && !initialCalendarData?.grid?.length) {
          setError(errorMsg);
        } else {
          // If we have previous data, just log the error but don't show it
          console.warn('Calendar API Error (using cached data):', errorMsg);
          setError(''); // Clear error if we have data
        }
        
        // Debug: Log error in development
        if (process.env.NODE_ENV === 'development') {
          console.error('Calendar API Error:', errorMsg, response);
        }
      }
    } catch (err: any) {
      const errorMsg = err?.message || 'Failed to load calendar data';
      // Only set error if we don't have any calendar data to show
      const hasData = calendarData.length > 0 || (initialCalendarData?.grid?.length ?? 0) > 0;
      if (!hasData) {
        setError(errorMsg);
        // Auto-retry up to 2 times
        if (retryCount < 2) {
          setTimeout(() => {
            setRetryCount(prev => prev + 1);
            loadCalendarData(true);
          }, 2000);
        }
      } else {
        // If we have previous data, just log the error but don't show it
        console.warn('Calendar API Exception (using cached data):', errorMsg);
        setError(''); // Clear error if we have data
      }
      
      // Debug: Log error in development
      if (process.env.NODE_ENV === 'development') {
        console.error('Calendar API Exception:', errorMsg, err);
      }
    } finally {
      setLoading(false);
    }
  }, [currentYear, currentMonth, calendarData.length, retryCount, updateEnglishYearFromCalendarData, initialCalendarData?.grid?.length]);

  useEffect(() => {
    if (!initialized || !currentYear || !currentMonth) {
      return;
    }
    if (skipFirstFetchRef.current) {
      skipFirstFetchRef.current = false;
      // If we have initial data, don't fetch again
      if ((initialCalendarData?.grid?.length ?? 0) > 0) {
        return;
      }
    }
    // Load calendar data whenever year or month changes
    loadCalendarData(false, true);
  }, [initialized, currentYear, currentMonth, loadCalendarData, initialCalendarData?.grid?.length]);

  // Listen for admin updates and reload the current month when calendar cell data changes
  useEffect(() => {
    const onCellDataUpdated = () => {
      if (typeof window === 'undefined') return;
      // Force reload to fetch updated calendar cell data
      console.log('📅 Calendar cell data updated - reloading month grid...');
      try { loadCalendarData(false, true); } catch (e) { console.warn('Failed to refresh calendar after cell update', e); }
    };

    if (typeof window !== 'undefined') {
      window.addEventListener('calendar:cellDataUpdated', onCellDataUpdated);
    }
    return () => {
      if (typeof window !== 'undefined') {
        window.removeEventListener('calendar:cellDataUpdated', onCellDataUpdated);
      }
    };
  }, [loadCalendarData]);

  useEffect(() => {
    const loadMine = () => {
      if (!isAuthenticated || !userReminderKey) {
        setReminders([]);
        return;
      }
      const allItems = readRemindersFromStorage();
      const mine = allItems
        .filter((item) => item.userKey === userReminderKey)
        .sort((a, b) => a.englishDate.localeCompare(b.englishDate));
      setReminders(mine);
    };
    loadMine();
    if (typeof window !== 'undefined') {
      window.addEventListener('calendar:remindersUpdated', loadMine);
    }
    return () => {
      if (typeof window !== 'undefined') {
        window.removeEventListener('calendar:remindersUpdated', loadMine);
      }
    };
  }, [isAuthenticated, userReminderKey]);

  useEffect(() => {
    const onDateSelected = (evt: Event) => {
      const customEvt = evt as CustomEvent;
      const nepaliDateStr = String(customEvt?.detail?.nepaliDateStr || '').trim();
      const englishDateStr = String(customEvt?.detail?.englishDateStr || '').trim();
      if (!nepaliDateStr || !englishDateStr) return;
      setSelectedDateInfo({ nepaliDate: nepaliDateStr, englishDate: englishDateStr });

      if (isAuthenticated) {
        openReminderEditorForDate(nepaliDateStr, englishDateStr);
      }
    };

    if (typeof window !== 'undefined') {
      window.addEventListener('calendar:dateSelected', onDateSelected as EventListener);
    }
    return () => {
      if (typeof window !== 'undefined') {
        window.removeEventListener('calendar:dateSelected', onDateSelected as EventListener);
      }
    };
  }, [isAuthenticated, openReminderEditorForDate]);

  useEffect(() => {
    if (!isAuthenticated || reminders.length === 0) return;

    const checkReminderDue = () => {
      const now = new Date();
      const sentMap = readReminderSentMap();

      for (const item of reminders) {
        if (item.snoozedUntil) {
          const snoozeKey = `${item.id}:snooze:${item.snoozedUntil}`;
          const snoozeDate = new Date(item.snoozedUntil);
          if (!Number.isNaN(snoozeDate.getTime()) && now >= snoozeDate && !sentMap[snoozeKey]) {
            setActiveReminderAlert({
              reminderId: item.id,
              trigger: 'Snoozed reminder',
              title: `Reminder: ${item.nepaliDate}`,
              message: item.note,
            });
            sentMap[snoozeKey] = true;
            writeReminderSentMap(sentMap);
            return;
          }
        }

        const [hour, minute] = item.reminderTime.split(':').map((v) => Number(v));
        const baseDate = new Date(`${item.englishDate}T00:00:00`);
        if (Number.isNaN(baseDate.getTime())) continue;

        const triggers: Array<{ label: string; when: Date }> = [];
        if (item.notifyTwoDays) {
          const t = new Date(baseDate);
          t.setDate(t.getDate() - 2);
          t.setHours(hour || 9, minute || 0, 0, 0);
          triggers.push({ label: '2 days before', when: t });
        }
        if (item.notifyOneDay) {
          const t = new Date(baseDate);
          t.setDate(t.getDate() - 1);
          t.setHours(hour || 9, minute || 0, 0, 0);
          triggers.push({ label: '1 day before', when: t });
        }
        if (item.notifySameDay) {
          const t = new Date(baseDate);
          t.setHours(hour || 9, minute || 0, 0, 0);
          triggers.push({ label: 'Same day', when: t });
        }

        for (const trigger of triggers) {
          const triggerKey = `${item.id}:${trigger.label}:${trigger.when.toISOString()}`;
          if (now >= trigger.when && !sentMap[triggerKey]) {
            setActiveReminderAlert({
              reminderId: item.id,
              trigger: trigger.label,
              title: `Reminder: ${item.nepaliDate} / ${item.englishDate}`,
              message: item.note,
            });
            sentMap[triggerKey] = true;
            writeReminderSentMap(sentMap);
            return;
          }
        }
      }
    };

    const id = setInterval(checkReminderDue, 30_000);
    checkReminderDue();
    return () => clearInterval(id);
  }, [isAuthenticated, reminders]);

  useEffect(() => {
    if (!activeReminderAlert || typeof window === 'undefined') return;
    if (!('Notification' in window)) return;

    if (Notification.permission === 'granted') {
      try {
        new Notification(activeReminderAlert.title, { body: activeReminderAlert.message });
      } catch {
        // Ignore notification failures and keep in-app alert.
      }
    } else if (Notification.permission !== 'denied') {
      Notification.requestPermission().catch(() => {});
    }
  }, [activeReminderAlert]);

  const openReminderEditor = useCallback(() => {
    if (!selectedDateInfo) return;
    if (!isAuthenticated) {
      router.push('/login');
      return;
    }

    openReminderEditorForDate(selectedDateInfo.nepaliDate, selectedDateInfo.englishDate);
  }, [isAuthenticated, openReminderEditorForDate, router, selectedDateInfo]);


  // --- Google Calendar Sync: Top-level helper ---
  function buildGoogleCalendarUrl(item: CalendarReminder) {
    const start = new Date(`${item.englishDate}T${item.reminderTime}:00`);
    const end = new Date(start.getTime() + 30 * 60 * 1000);
    if (Number.isNaN(start.getTime())) return '#';

    const params = new URLSearchParams({
      action: 'TEMPLATE',
      text: `Reminder - ${item.nepaliDate}`,
      dates: `${formatGoogleDateTime(start)}/${formatGoogleDateTime(end)}`,
      details: `${item.note}\nNepali Date: ${item.nepaliDate}\nEnglish Date: ${item.englishDate}`,
      ctz: 'Asia/Kathmandu',
    });

    return `https://calendar.google.com/calendar/render?${params.toString()}`;
  }

  const saveReminder = useCallback(() => {
    if (!selectedDateInfo || !userReminderKey) return;
    const trimmedNote = noteInput.trim();
    if (!trimmedNote) {
      setReminderError('Please enter a reminder note.');
      return;
    }
    if (!notifyTwoDays && !notifyOneDay && !notifySameDay) {
      setReminderError('Select at least one reminder day (2 days, 1 day, or same day).');
      return;
    }

    const nowIso = new Date().toISOString();
    const id = editingReminderId || `rem_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

    const payload: CalendarReminder = {
      id,
      userKey: userReminderKey,
      nepaliDate: selectedDateInfo.nepaliDate,
      englishDate: selectedDateInfo.englishDate,
      note: trimmedNote,
      reminderTime,
      notifyTwoDays,
      notifyOneDay,
      notifySameDay,
      snoozedUntil: null,
      createdAt: nowIso,
      updatedAt: nowIso,
    };

    persistUserReminders((current) => {
      const existingIndex = current.findIndex((item) => item.id === id);
      if (existingIndex >= 0) {
        const updated = [...current];
        updated[existingIndex] = {
          ...updated[existingIndex],
          ...payload,
          createdAt: updated[existingIndex].createdAt,
        };
        return updated;
      }

      const deduped = current.filter(
        (item) => !(item.nepaliDate === payload.nepaliDate && item.englishDate === payload.englishDate)
      );
      return [...deduped, payload];
    });

    // Automatically open Google Calendar event creation page
    try {
      const url = buildGoogleCalendarUrl(payload);
      if (url && url !== '#') {
        window.open(url, '_blank', 'noopener');
      }
    } catch (e) {
      // Ignore errors
    }
    // Do NOT close the modal after saving; user must close manually
    // setReminderModalOpen(false);
  }, [editingReminderId, noteInput, notifyOneDay, notifySameDay, notifyTwoDays, persistUserReminders, reminderTime, selectedDateInfo, userReminderKey, buildGoogleCalendarUrl]);

  const deleteReminder = useCallback((id: string) => {
    persistUserReminders((current) => current.filter((item) => item.id !== id));
    if (editingReminderId === id) {
      setReminderModalOpen(false);
    }
  }, [editingReminderId, persistUserReminders]);

  const snoozeReminder = useCallback((id: string, targetIso: string) => {
    persistUserReminders((current) =>
      current.map((item) =>
        item.id === id
          ? { ...item, snoozedUntil: targetIso, updatedAt: new Date().toISOString() }
          : item
      )
    );
    setActiveReminderAlert(null);
  }, [persistUserReminders]);


  // Scroll to today after calendar data loads
  useEffect(() => {
    if (shouldScrollToToday && !loading && calendarData.length > 0) {
      const todayCell = document.querySelector('[data-is-today="true"]');
      if (todayCell) {
        // Use requestAnimationFrame for better timing
        requestAnimationFrame(() => {
          todayCell.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
          // Add a highlight animation
          todayCell.classList.add('animate-pulse');
          setTimeout(() => {
            todayCell.classList.remove('animate-pulse');
          }, 1000);
        });
        setShouldScrollToToday(false);
      }
    }
  }, [shouldScrollToToday, loading, calendarData]);
  
  const handleGoToDate = () => {
    setCurrentYear(selectedYear);
    setCurrentMonth(selectedMonth);
    setSelectedDate(null); // Clear selected date when navigating to new month
    // Reset English year - it will be updated when calendar data loads
    setEnglishYear(selectedYear - 57);
  };

  // Get today's Nepali date from context for highlighting
  const todayNepaliDate = useMemo(() => {
    if (dateData?.nepali) {
      return {
        year: dateData.nepali.year,
        month: dateData.nepali.month,
        day: dateData.nepali.day,
      };
    }
    if (initialDateData?.nepali) {
      return {
        year: initialDateData.nepali.year,
        month: initialDateData.nepali.month,
        day: initialDateData.nepali.day,
      };
    }
    return null;
  }, [dateData, initialDateData]);

  const todayEnglishDate = useMemo(() => {
    if (dateData?.english) {
      return {
        year: dateData.english.year,
        month: dateData.english.month,
        day: dateData.english.day,
      };
    }
    if (initialDateData?.english) {
      return {
        year: initialDateData.english.year ?? 0,
        month: initialDateData.english.month ?? 0,
        day: initialDateData.english.day ?? 0,
      };
    }
    return null;
  }, [dateData, initialDateData]);

  // Browser-local English date as a final fallback (guards against API timezone drift)
  const todayLocalEnglish = useMemo(() => {
    const now = new Date();
    return {
      year: now.getFullYear(),
      month: now.getMonth() + 1,
      day: now.getDate(),
    };
  }, [dateData]);

  const goToToday = async () => {
    try {
      const response: any = await apiService.getCurrentDate();
      if (response?.success && response?.data) {
        const year = response.data.nepali.year;
        const month = response.data.nepali.month;

        // Always clear selected date when clicking "आज" (Today)
        setSelectedDate(null);

        // Only navigate if not already in current month
        const isAlreadyInCurrentMonth = currentYear === year && currentMonth === month;
        if (!isAlreadyInCurrentMonth) {
          setCurrentYear(year);
          setCurrentMonth(month);
          setSelectedYear(year);
          setSelectedMonth(month);
          if (response.data.english) {
            setEnglishYear(response.data.english.year);
          }
        }

        // Trigger scroll to today's date
        setShouldScrollToToday(true);
      }
    } catch (err) {
      // Silently fail - don't show error to user
    }
  };

  const handleMonthChange = (direction: 'prev' | 'next') => {
    // Clear selected date when changing months - user should see today's date if in current month
    setSelectedDate(null);

    if (direction === 'prev') {
      if (currentMonth === 1) {
        const newYear = currentYear - 1;
        setCurrentMonth(12);
        setCurrentYear(newYear);
        setSelectedMonth(12);
        setSelectedYear(newYear);
      } else {
        const newMonth = currentMonth - 1;
        setCurrentMonth(newMonth);
        setSelectedMonth(newMonth);
      }
    } else {
      if (currentMonth === 12) {
        const newYear = currentYear + 1;
        setCurrentMonth(1);
        setCurrentYear(newYear);
        setSelectedMonth(1);
        setSelectedYear(newYear);
      } else {
        const newMonth = currentMonth + 1;
        setCurrentMonth(newMonth);
        setSelectedMonth(newMonth);
      }
    }
  };

  const currentMonthReminders = useMemo(() => {
    const monthPrefix = `${currentYear}-${String(currentMonth).padStart(2, '0')}`;
    return reminders
      .filter((item) => item.nepaliDate.startsWith(monthPrefix))
      .sort((a, b) => a.nepaliDate.localeCompare(b.nepaliDate));
  }, [currentMonth, currentYear, reminders]);

  const selectedDateReminder = useMemo(() => {
    if (!selectedDateInfo) return null;
    return reminders.find(
      (item) => item.nepaliDate === selectedDateInfo.nepaliDate && item.englishDate === selectedDateInfo.englishDate
    ) || null;
  }, [reminders, selectedDateInfo]);

  // Get first day of week for the month
  // Backend already includes null values for empty cells, so we don't need to calculate offset
  // We'll just render null values as empty cells

  return (
    <div className="bg-white dark:bg-dark-800 rounded-lg sm:rounded-xl shadow-lg sm:shadow-xl p-2 sm:p-5 md:p-7 lg:p-8 border border-gray-200 dark:border-gray-700 w-full max-w-full overflow-visible" style={{ overflow: 'visible' }}>
      {/* Calendar Header */}
      <div className="flex flex-col gap-4 mb-4 sm:mb-6">
        {/* Top Section: Mobile - Two boxes with आज in between, Desktop - Month name + boxes with आज in between */}
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-4">
          {/* Desktop: Large Month Name */}
          <div className="hidden sm:block">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 dark:text-white devanagari">
              {nepaliMonths[currentMonth - 1]?.name || ''}
            </h2>
          </div>

          {/* Date boxes with आज in between */}
          <div className="flex items-center gap-2 w-full sm:w-auto">
            {/* Nepali Date Box - Left */}
            <div className="flex-1 sm:flex-none bg-white dark:bg-dark-700 px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-600">
              <div className="text-sm sm:text-base font-semibold text-gray-900 dark:text-white devanagari text-center sm:text-left">
                {toDevanagariNumeral(currentYear)} {nepaliMonths[currentMonth - 1]?.name || ''}
              </div>
            </div>
            
            {/* Red Today Button - In between the boxes */}
            <button 
              onClick={goToToday} 
              className="bg-[#8F1B1B] hover:bg-[#7a1515] text-white font-semibold text-sm px-3 sm:px-4 py-2 rounded-md shadow-md hover:shadow-lg transition-all duration-200 flex items-center justify-center flex-shrink-0"
              aria-label="Go to today"
            >
              <span className="devanagari">आज</span>
            </button>

            {/* English Date Range Box - Right */}
            {englishMonthRange && (
              <div className="flex-1 sm:flex-none bg-white dark:bg-dark-700 px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-600">
                <div className="text-sm sm:text-base font-semibold text-gray-600 dark:text-gray-400 text-center">
                  {englishMonthRange}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* White Panel with Year/Month Selectors and Navigation */}
        <div className="relative flex items-center justify-center gap-2 sm:gap-4">
          {/* Left Chevron */}
          <button
            onClick={() => handleMonthChange('prev')}
            className="p-2 hover:bg-gray-100 dark:hover:bg-dark-700 rounded-lg transition-colors focus-ring flex-shrink-0"
            aria-label="Previous month"
            type="button"
          >
            <ChevronLeft className="w-5 h-5 text-gray-700 dark:text-gray-300 flex-shrink-0" width={20} height={20} />
          </button>

          {/* White Panel with Year/Month/Go - All in one box on mobile */}
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-center gap-2 sm:gap-4 p-3 sm:p-5 bg-white dark:bg-dark-800 rounded-xl border-2 border-gray-200 dark:border-gray-600 shadow-md flex-1 min-w-0">
            {/* Year Selector */}
            <div className="flex items-center gap-1.5 sm:gap-2 flex-1 sm:flex-none min-w-0">
              <label htmlFor="year-select" className="text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 whitespace-nowrap flex-shrink-0">
                <span className="devanagari">वर्ष:</span> Year:
              </label>
              <select
                id="year-select"
                value={selectedYear}
                onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                className="flex-1 sm:min-w-[140px] px-2 sm:px-3 py-1.5 sm:py-2 text-xs sm:text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-dark-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all appearance-none cursor-pointer"
              >
                {yearsList.map((year) => (
                  <option key={year} value={year}>
                    {formatYearWithEnglishRange(year)}
                  </option>
                ))}
              </select>
            </div>

            {/* Month Selector */}
            <div className="flex items-center gap-1.5 sm:gap-2 flex-1 sm:flex-none min-w-0">
              <label htmlFor="month-select" className="text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 whitespace-nowrap flex-shrink-0">
                <span className="devanagari">महिना:</span> Month:
              </label>
              <select
                id="month-select"
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
                className="flex-1 sm:min-w-[140px] px-2 sm:px-3 py-1.5 sm:py-2 text-xs sm:text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-dark-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all appearance-none cursor-pointer"
              >
                {nepaliMonths.map((month) => (
                  <option key={month.num} value={month.num}>
                    {month.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Go Button - Inside box on mobile, stays in box on desktop */}
            <button
              onClick={handleGoToDate}
              className="w-full sm:w-auto px-3 sm:px-4 py-1.5 sm:py-2 bg-[#8F1B1B] hover:bg-[#7a1515] text-white font-semibold text-xs sm:text-sm rounded-md shadow-md hover:shadow-lg transform hover:scale-105 transition-all duration-200 flex items-center justify-center gap-1.5 sm:gap-2 flex-shrink-0"
              aria-label="Go to selected date"
            >
              <Search className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
              Go
            </button>
          </div>

          {/* Right Chevron */}
          <button
            onClick={() => handleMonthChange('next')}
            className="p-2 hover:bg-gray-100 dark:hover:bg-dark-700 rounded-lg transition-colors focus-ring flex-shrink-0"
            aria-label="Next month"
            type="button"
          >
            <ChevronRight className="w-5 h-5 text-gray-700 dark:text-gray-300 flex-shrink-0" width={20} height={20} />
          </button>
        </div>
      </div>

      {loading && calendarData.length === 0 ? (
        <div className="flex items-center justify-center py-12">
          <div className="spinner"></div>
          <p className="ml-4 text-gray-600 dark:text-gray-400">Loading calendar...</p>
        </div>
      ) : error && calendarData.length === 0 && !initialCalendarData?.grid?.length ? (
        <div className="text-center py-12">
          <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-6 mb-4">
            <p className="text-yellow-700 dark:text-yellow-400 font-medium mb-2">
              Unable to load calendar data
            </p>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              {typeof error === 'string' ? error : (error as any)?.message || 'An error occurred'}
            </p>
            <button 
              onClick={() => {
                setError('');
                setRetryCount(0);
                loadCalendarData(false);
              }} 
              className="px-4 py-2 bg-yellow-600 hover:bg-yellow-700 text-white rounded-md transition-colors"
            >
              Retry
            </button>
          </div>
        </div>
      ) : calendarData.length === 0 && !initialCalendarData?.grid?.length ? (
        <div className="text-center py-12 text-gray-600 dark:text-gray-400">
          <p className="mb-4">No calendar data available</p>
          <button 
            onClick={() => {
              setError('');
              setRetryCount(0);
              loadCalendarData(false);
            }} 
            className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-md transition-colors"
          >
            Retry
          </button>
        </div>
      ) : (
        <>
          {/* Weekday Headers - Nepali names */}
          <div className="grid grid-cols-7 gap-px sm:gap-1.5 md:gap-2 mb-2 sm:mb-3 md:mb-4">
            {weekdays.map((day, index) => (
              <div
                key={index}
                className={`text-center py-1 sm:py-1.5 md:py-2 font-semibold ${
                  index === 6
                    ? 'text-primary-600 dark:text-primary-400'
                    : 'text-gray-700 dark:text-gray-300'
                }`}
              >
                <div className="text-sm sm:text-xs md:text-sm devanagari font-semibold">{typeof day.ne === 'string' ? day.ne : String(day.ne || '')}</div>
                <div className="text-[10px] sm:text-[10px] text-gray-500 dark:text-gray-500 hidden sm:block">{typeof day.en === 'string' ? day.en : String(day.en || '')}</div>
              </div>
            ))}
          </div>

          {/* Calendar Days Grid - Responsive with flexible gaps */}
          <div className="grid grid-cols-7 gap-0.5 sm:gap-1 md:gap-1.5 lg:gap-2">
            {/* Calendar days - backend includes null values for empty cells */}
            {calendarData.map((day: CalendarDay | null, index: number) => {
              // Handle null values (empty cells before month starts)
              if (day === null) {
                return (
                  <div
                    key={`empty-${index}`}
                    className="aspect-square w-full"
                  />
                );
              }
              // Explicitly ensure boolean type - consider both event entries and calendar cell data from admin
              const hasEvents: boolean = (Array.isArray(day.events) && day.events.length > 0) || Boolean(day.calendarCellData?.event);
              
              // Get English day number - parse directly from date string to avoid timezone issues
              // IMPORTANT: English date should correspond to the same calendar day as Nepali date
              let englishDay = '';
              let englishYear = 0;
              let englishMonth = 0;
              let englishDayNum = 0;
              
              if (day.english) {
                if (typeof day.english === 'string') {
                  // Parse date string (format: YYYY-MM-DD) - parse as local date to avoid timezone shifts
                  const dateParts = day.english.split('-');
                  if (dateParts.length >= 3) {
                    englishYear = parseInt(dateParts[0], 10);
                    englishMonth = parseInt(dateParts[1], 10);
                    englishDayNum = parseInt(dateParts[2], 10);
                    // Remove leading zero for display - ensure it's always a string
                    englishDay = String(englishDayNum || '');
                  }
                } else if (day.english && typeof day.english === 'object' && 'date' in day.english && day.english.date) {
                  // Parse date string from object (format: YYYY-MM-DD) - parse as local date
                  const dateParts = String(day.english.date).split('-');
                  if (dateParts.length >= 3) {
                    englishYear = parseInt(dateParts[0], 10);
                    englishMonth = parseInt(dateParts[1], 10);
                    englishDayNum = parseInt(dateParts[2], 10);
                    // Remove leading zero for display - ensure it's always a string
                    englishDay = String(englishDayNum || '');
                  }
                } else if (day.english && typeof day.english === 'object' && 'day' in day.english && day.english.day) {
                  // Use day, month, year directly if available
                  englishYear = Number(day.english.year) || 0;
                  englishMonth = Number(day.english.month) || 0;
                  englishDayNum = Number(day.english.day) || 0;
                  // Ensure it's always a string
                  englishDay = String(englishDayNum || '');
                }
              }
              
              // Verify that English date matches Nepali date position
              // Both dates should refer to the same calendar day in their respective calendars
              // The backend should ensure this alignment when generating the grid
              
              // Check if today - use Nepali date from server/context as primary source of truth
              // This ensures we match the exact date the server considers "today"
              const isTodayNepali: boolean = !!(todayNepaliDate && 
                                   day.nepali && 
                                   currentYear === todayNepaliDate.year &&
                                   currentMonth === todayNepaliDate.month &&
                                   Number(day.nepali.day) === Number(todayNepaliDate.day));
              
              // Only use English date fallback if Nepali date is not available from server
              const isTodayEnglish: boolean = !todayNepaliDate && !!(todayEnglishDate &&
                                     englishYear === todayEnglishDate.year && 
                                     englishMonth === todayEnglishDate.month && 
                                     englishDayNum === todayEnglishDate.day);
              
              // Final fallback: use browser local date only if no server date is available
              const isTodayLocal: boolean = !todayNepaliDate && !todayEnglishDate && !!(
                                     englishYear === todayLocalEnglish.year && 
                                     englishMonth === todayLocalEnglish.month && 
                                     englishDayNum === todayLocalEnglish.day);
              
              // Explicitly type as boolean - prioritize Nepali date from server
              const isToday: boolean = isTodayNepali || isTodayEnglish || isTodayLocal;

              // Check if this date is selected
              const isSelected: boolean = !!(selectedDate &&
                selectedDate.year === currentYear &&
                selectedDate.month === currentMonth &&
                selectedDate.day === Number(day.nepali?.day));

              // Saturday flag for styling (index 6 in the 0-based week grid)
              const isSaturday = index % 7 === 6;
              
              // Get Nepali date in Devanagari numerals - ensure it's always a string
              const nepaliDayNum = day.nepali?.day ? Number(day.nepali.day) : null;
              const nepaliDayNumeral = nepaliDayNum !== null && !isNaN(nepaliDayNum) ? toDevanagariNumeral(nepaliDayNum) : '';

              // Custom onDateSelect to open event popup
              const handleDateSelect = (date: {year: number, month: number, day: number}) => {
                setSelectedDate(date);
                // Find events for this date
                let events: any[] = [];
                if (Array.isArray(day.events) && day.events.length > 0) {
                  events = day.events;
                }
                // Optionally include admin cell event
                if (day.calendarCellData?.event) {
                  events = [...events, { ...day.calendarCellData, isAdminEvent: true }];
                }
                setSelectedDateEvents(events);
                setEventPopupOpen(true);
              };
              return (
                <CalendarDayCell
                  key={index}
                  day={day}
                  index={index}
                  isToday={isToday}
                  isSaturday={isSaturday}
                  isSelected={isSelected}
                  nepaliDayNumeral={nepaliDayNumeral}
                  englishDay={englishDay}
                  hasEvents={hasEvents}
                  currentMonth={currentMonth}
                  currentYear={currentYear}
                  onDateSelect={handleDateSelect}
                />
              );
                  {/* Event Popup Modal */}
                  {eventPopupOpen && selectedDate !== null && (() => {
                    const { year, month, day } = selectedDate as { year: number; month: number; day: number };
                    return (
                      <div className="fixed inset-0 z-[200] bg-black/40 flex items-center justify-center p-4">
                        <div className="w-full max-w-md rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-dark-800 p-5 space-y-4 relative">
                          <button
                            className="absolute top-2 right-2 text-gray-500 hover:text-gray-900 dark:hover:text-white text-xl font-bold"
                            onClick={() => setEventPopupOpen(false)}
                            aria-label="Close event popup"
                          >
                            ×
                          </button>
                          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                            Events for {year}-{String(month).padStart(2, '0')}-{String(day).padStart(2, '0')}
                          </h3>
                          {selectedDateEvents.length === 0 ? (
                            <p className="text-gray-600 dark:text-gray-300">No events for this date.</p>
                          ) : (
                            <ul className="space-y-3">
                              {selectedDateEvents.map((event, idx) => (
                                <li key={idx} className="rounded-lg border border-gray-200 dark:border-gray-700 p-3 bg-gray-50 dark:bg-dark-900/40">
                                  <div className="font-semibold text-primary-700 dark:text-primary-400">
                                    {event.title || event.event || 'Untitled Event'}
                                  </div>
                                  {event.description && (
                                    <div className="text-sm text-gray-700 dark:text-gray-200 mt-1">{event.description}</div>
                                  )}
                                  {event.isAdminEvent && (
                                    <div className="text-xs text-blue-600 dark:text-blue-400 mt-1">(Admin Event)</div>
                                  )}
                                  {/* Show time if available */}
                                  {event.time && (
                                    <div className="text-xs text-gray-500 mt-1">Time: {event.time}</div>
                                  )}
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>
                      </div>
                    );
                  })()}
            })}
          </div>

          {isAuthenticated && selectedDateInfo && (
            <div className="hidden sm:block mt-5 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-dark-900/40 p-4">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div>
                  <p className="text-sm font-semibold text-gray-900 dark:text-white">Selected Date</p>
                  <p className="text-xs text-gray-600 dark:text-gray-300">
                    Nepali: {selectedDateInfo.nepaliDate} | English: {selectedDateInfo.englishDate}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={openReminderEditor}
                  className="inline-flex items-center justify-center px-4 py-2 rounded-lg bg-primary-600 text-white text-sm font-semibold hover:bg-primary-700"
                >
                  {selectedDateReminder ? 'Edit Reminder Note' : 'Add Reminder Note'}
                </button>
              </div>
            </div>
          )}

          {isAuthenticated && (
            <div className="hidden sm:block mt-5 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-dark-800 p-4 sm:p-5">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white">
                  Reminder Notes ({currentMonthReminders.length})
                </h3>
                <button
                  type="button"
                  className="px-3 py-1.5 rounded-md text-xs font-semibold border border-primary-300 text-primary-600 dark:text-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900"
                  onClick={() => {
                    // Open modal for new reminder, let user pick date
                    setEditingReminderId(null);
                    setNoteInput('');
                    setReminderTime('09:00');
                    setNotifyTwoDays(true);
                    setNotifyOneDay(true);
                    setNotifySameDay(true);
                    setReminderError('');
                    setSelectedDateInfo(null); // Will pick date in modal
                    setReminderModalOpen(true);
                  }}
                >
                  + Add Reminder
                </button>
              </div>
              {currentMonthReminders.length === 0 ? (
                <p className="text-sm text-gray-600 dark:text-gray-400">No reminder notes for this Nepali month.</p>
              ) : (
                <div className="space-y-3">
                  {currentMonthReminders.map((item) => (
                    <div key={item.id} className="rounded-lg border border-gray-200 dark:border-gray-700 p-3">
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        Nepali: {item.nepaliDate} | English: {item.englishDate} at {item.reminderTime}
                      </p>
                      <p className="text-sm text-gray-900 dark:text-white mt-1 whitespace-pre-wrap">{item.note}</p>
                      <div className="mt-2 flex flex-wrap gap-2">
                        <button
                          type="button"
                          onClick={() => {
                            setSelectedDateInfo({ nepaliDate: item.nepaliDate, englishDate: item.englishDate });
                            setTimeout(() => openReminderEditor(), 0);
                          }}
                          className="px-3 py-1.5 rounded-md text-xs font-semibold border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200"
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          onClick={() => deleteReminder(item.id)}
                          className="px-3 py-1.5 rounded-md text-xs font-semibold border border-red-300 text-red-600 dark:text-red-400"
                        >
                          Delete
                        </button>
                        <a
                          href={buildGoogleCalendarUrl(item)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="px-3 py-1.5 rounded-md text-xs font-semibold border border-primary-300 text-primary-600 dark:text-primary-400"
                        >
                          Google Calendar
                        </a>
                        <button
                          type="button"
                          className="px-3 py-1.5 rounded-md text-xs font-semibold border border-blue-300 text-blue-600 dark:text-blue-400"
                          onClick={() => window.open(`mailto:?subject=Reminder%20for%20${encodeURIComponent(item.nepaliDate)}%20|%20${encodeURIComponent(item.englishDate)}&body=${encodeURIComponent(item.note + '\nNepali: ' + item.nepaliDate + '\nEnglish: ' + item.englishDate + '\nTime: ' + item.reminderTime)}`)}
                        >
                          Email Reminder
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </>
      )}

      {reminderModalOpen && (
        <div className="fixed inset-0 z-[170] bg-black/50 flex items-center justify-center p-4">
          <div className="w-full max-w-lg rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-dark-800 p-5 space-y-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              {editingReminderId ? 'Edit Reminder Note' : 'Add Reminder Note'}
            </h3>
            {/* Date picker for new reminder */}
            {!editingReminderId && (
              <div className="mb-2">
                <label className="text-xs font-semibold text-gray-700 dark:text-gray-300">Select Date</label>
                <div className="flex gap-2 mt-1">
                  <input
                    type="date"
                    className="input"
                    value={selectedDateInfo?.englishDate || ''}
                    onChange={e => {
                      const engDate = e.target.value;
                      // Try to find matching Nepali date from calendarData
                      let nepaliDate = '';
                      for (const day of calendarData) {
                        if (day && day.english) {
                          let eng = '';
                          if (typeof day.english === 'string') eng = day.english;
                          else if (typeof day.english === 'object' && 'date' in day.english && day.english.date) eng = String(day.english.date);
                          if (eng === engDate && day.nepali) {
                            nepaliDate = `${day.nepali.year}-${String(day.nepali.month).padStart(2, '0')}-${String(day.nepali.day).padStart(2, '0')}`;
                            break;
                          }
                        }
                      }
                      setSelectedDateInfo({ nepaliDate, englishDate: engDate });
                    }}
                  />
                  {/* Optionally show Nepali date if available */}
                  {selectedDateInfo?.nepaliDate && (
                    <span className="text-xs text-gray-500 dark:text-gray-400 self-center">Nepali: {selectedDateInfo.nepaliDate}</span>
                  )}
                </div>
              </div>
            )}
            {selectedDateInfo && (
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Nepali: {selectedDateInfo.nepaliDate} | English: {selectedDateInfo.englishDate}
              </p>
            )}

            <textarea
              className="input min-h-[110px]"
              placeholder="Write your reminder note"
              value={noteInput}
              onChange={(e) => setNoteInput(e.target.value)}
            />

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-semibold text-gray-700 dark:text-gray-300">Reminder Time</label>
                <input
                  type="time"
                  className="input mt-1"
                  value={reminderTime}
                  onChange={(e) => setReminderTime(e.target.value)}
                />
              </div>
              <div className="text-xs text-gray-600 dark:text-gray-300 space-y-2 pt-1">
                <label className="flex items-center gap-2">
                  <input type="checkbox" checked={notifyTwoDays} onChange={(e) => setNotifyTwoDays(e.target.checked)} />
                  2 days before
                </label>
                <label className="flex items-center gap-2">
                  <input type="checkbox" checked={notifyOneDay} onChange={(e) => setNotifyOneDay(e.target.checked)} />
                  1 day before
                </label>
                <label className="flex items-center gap-2">
                  <input type="checkbox" checked={notifySameDay} onChange={(e) => setNotifySameDay(e.target.checked)} />
                  Same day
                </label>
              </div>
            </div>

            {reminderError && <p className="text-xs text-red-600">{reminderError}</p>}

            <div className="flex flex-wrap justify-end gap-2">
              <button
                type="button"
                className="px-3 py-1.5 rounded-md text-xs font-semibold border border-blue-300 text-blue-600 dark:text-blue-400"
                onClick={() => window.open(`mailto:?subject=Reminder%20for%20${encodeURIComponent(selectedDateInfo?.nepaliDate || '')}%20|%20${encodeURIComponent(selectedDateInfo?.englishDate || '')}&body=${encodeURIComponent(noteInput + '\nNepali: ' + (selectedDateInfo?.nepaliDate || '') + '\nEnglish: ' + (selectedDateInfo?.englishDate || '') + '\nTime: ' + reminderTime)}`)}
              >
                Email Reminder
              </button>
              {editingReminderId && (
                <button
                  type="button"
                  onClick={() => editingReminderId && deleteReminder(editingReminderId)}
                  className="px-3 py-2 rounded-lg border border-red-300 text-red-600 text-sm font-semibold"
                >
                  Delete
                </button>
              )}
              <button
                type="button"
                onClick={() => setReminderModalOpen(false)}
                className="px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-sm font-semibold"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={saveReminder}
                className="px-4 py-2 rounded-lg bg-primary-600 text-white text-sm font-semibold"
                disabled={!selectedDateInfo || !selectedDateInfo.englishDate}
              >
                Save Reminder
              </button>
            </div>
          </div>
        </div>
      )}

      {activeReminderAlert && (
        <div className="fixed bottom-4 right-4 z-[180] w-[min(92vw,420px)] rounded-xl border border-primary-300 dark:border-primary-700 bg-white dark:bg-dark-800 shadow-2xl p-4 space-y-3">
          <div>
            <p className="text-sm font-bold text-gray-900 dark:text-white">{activeReminderAlert.title}</p>
            <p className="text-xs text-primary-600 dark:text-primary-400">{activeReminderAlert.trigger}</p>
            <p className="mt-1 text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">{activeReminderAlert.message}</p>
          </div>
          <div className="flex flex-wrap gap-2 justify-end">
            <button
              type="button"
              onClick={() => setActiveReminderAlert(null)}
              className="px-3 py-1.5 rounded-md text-xs font-semibold border border-gray-300 dark:border-gray-600"
            >
              Dismiss
            </button>
            <button
              type="button"
              onClick={() => {
                const target = new Date(Date.now() + 60 * 60 * 1000).toISOString();
                snoozeReminder(activeReminderAlert.reminderId, target);
              }}
              className="px-3 py-1.5 rounded-md text-xs font-semibold border border-primary-300 text-primary-600"
            >
              Remind in 1 hour
            </button>
            <button
              type="button"
              onClick={() => {
                const picked = window.prompt('Enter reminder datetime (YYYY-MM-DDTHH:mm)');
                if (!picked) return;
                const custom = new Date(picked);
                if (Number.isNaN(custom.getTime())) return;
                snoozeReminder(activeReminderAlert.reminderId, custom.toISOString());
              }}
              className="px-3 py-1.5 rounded-md text-xs font-semibold border border-primary-300 text-primary-600"
            >
              Specific time
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// Memoize CalendarGrid to prevent unnecessary re-renders for better INP
export default memo(CalendarGrid);

